import { ref, Ref, toRaw } from "vue";
import ApiManager from "~/code/api-manager";
import { useRouter } from "vue-router";
import { AuthorizationRequest } from "~/generated/models/authorization-request";
import { ForgotPasswordRequest } from "~/generated/models/forgot-password-request";

import { EventBus } from "~/code/event-bus";
import {
  OnibiPortalUserDto,
  ResetPasswordDto,
  UserTokenResponse,
} from "~/generated/models";
import { useServerEnvironmentStore } from "~/store/server-environment-store";
import { useUserStore } from "~/store/user-store";

class AuthManager {
  private static instance: AuthManager;
  public user: Ref<OnibiPortalUserDto | null> = ref(null);
  public authenticated: Ref<boolean> = ref(false);
  public loading: Ref<boolean> = ref(false);

  private apiManager = ApiManager.getInstance();
  public router = useRouter();
  private constructor() {
    const authenticated =
      localStorage.getItem("authenticated") === "true" ||
      sessionStorage.getItem("authenticated") === "true";
    const userString =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    try {
      if (authenticated && userString && userString !== "undefined") {
        this.authenticated.value = authenticated;
        this.user.value = JSON.parse(userString);
        void this.hydrateUserPermissionNames();
      }
    } catch (e) {
      this.authenticated.value = false;
      this.user.value = null;
    }

    this.populateUserFromToken();
  }

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private async hydrateUserPermissionNames() {
    const currentUser = this.user.value;
    const currentPermissions = (currentUser?.permissions || []) as unknown[];

    if (!currentUser || currentPermissions.length === 0) {
      return;
    }

    const hasNumericPermissions = currentPermissions.some((permission) => {
      return (
        typeof permission === "number" ||
        (typeof permission === "string" && /^\d+$/.test(permission.trim()))
      );
    });

    if (!hasNumericPermissions) {
      return;
    }

    const userStore = useUserStore();
    await userStore.ensurePermissionsLoaded();

    const mappedPermissions = currentPermissions
      .map((permission) => {
        if (typeof permission === "number" && Number.isFinite(permission)) {
          return userStore.permissionNameById(permission);
        }

        if (typeof permission === "string") {
          const trimmed = permission.trim();

          if (/^\d+$/.test(trimmed)) {
            return userStore.permissionNameById(parseInt(trimmed, 10));
          }

          return trimmed;
        }

        return null;
      })
      .filter((permission): permission is string =>
        Boolean(permission && permission.trim().length > 0),
      );

    this.user.value = {
      ...currentUser,
      permissions: mappedPermissions,
    };
  }

  async setAuthState(
    userTokenResponse: UserTokenResponse,
    rememberMe: boolean = true,
  ) {
    this.user.value = userTokenResponse.user;
    await this.hydrateUserPermissionNames();
    this.authenticated.value = true;
    this.apiManager.updateAccessToken(userTokenResponse.token);

    const storage = rememberMe ? localStorage : sessionStorage;

    // Record the rememberMe preference so tryRefreshToken knows which storage to use
    localStorage.setItem("rememberMe", rememberMe.toString());

    // Clear the opposite storage to avoid stale tokens
    const oppositeStorage = rememberMe ? sessionStorage : localStorage;
    oppositeStorage.removeItem("authenticated");
    oppositeStorage.removeItem("bearerToken");
    oppositeStorage.removeItem("refreshToken");
    oppositeStorage.removeItem("refreshTokenExpires");
    oppositeStorage.removeItem("user");

    storage.setItem("authenticated", "true");
    storage.setItem("bearerToken", userTokenResponse.token);
    if (rememberMe) {
      storage.setItem("refreshToken", userTokenResponse.refreshToken ?? "");
      storage.setItem(
        "refreshTokenExpires",
        userTokenResponse.refreshTokenExpiryTime
          ? userTokenResponse.refreshTokenExpiryTime.toString()
          : "",
      );
    } else {
      storage.removeItem("refreshToken");
      storage.removeItem("refreshTokenExpires");
    }
    storage.setItem("user", this.safeStringify(toRaw(this.user.value)));

    const serverEnvironmentStore = useServerEnvironmentStore();
    serverEnvironmentStore.reset();
    serverEnvironmentStore.init();
  }

  unSetAuthState() {
    this.user.value = null;
    this.authenticated.value = false;

    this.apiManager.updateAccessToken("");

    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem("user");
      storage.removeItem("bearerToken");
      storage.removeItem("refreshToken");
      storage.removeItem("refreshTokenExpires");
      storage.setItem("authenticated", "false");
    }
    localStorage.removeItem("rememberMe");
  }

  safeStringify(obj: any, depth = 5) {
    let currentDepth = 0;
    const cache = new Set();
    return JSON.stringify(obj, (key, value) => {
      if (currentDepth > depth) return;

      if (typeof value === "object" && value !== null) {
        if (cache.has(value)) return;
        cache.add(value);
      }

      currentDepth++;
      let result = value;
      currentDepth--;
      return result;
    });
  }
  async tryRefreshToken(): Promise<boolean> {
    const rememberedStorage =
      localStorage.getItem("rememberMe") === "true"
        ? localStorage
        : sessionStorage;
    const fallbackStorage =
      rememberedStorage === localStorage ? sessionStorage : localStorage;

    const rememberedRefreshToken = rememberedStorage.getItem("refreshToken");
    const rememberedUserString = rememberedStorage.getItem("user");
    const hasRememberedAuthData =
      !!rememberedRefreshToken && !!rememberedUserString;

    const storage = hasRememberedAuthData ? rememberedStorage : fallbackStorage;
    const refreshToken = storage.getItem("refreshToken");
    const userString = storage.getItem("user");

    if (!refreshToken || !userString) return false;

    let username: string | null = null;
    try {
      username = JSON.parse(userString)?.userName ?? null;
    } catch {
      return false;
    }
    if (!username) return false;

    try {
      const response = await this.apiManager.apis[
        "AuthApi"
      ].apiAuthRefreshTokenPost({
        username,
        refreshToken,
      });
      if (response.status === 200) {
        await this.setAuthState(response.data, storage === localStorage);
        return true;
      }
    } catch {
      // refresh failed
    }
    return false;
  }

  async login(name: string, password: string, rememberMe: boolean = false) {
    console.log("logging in");
    this.loading.value = true;

    const post: AuthorizationRequest = {
      username: name,
      password: password,
      rememberMe,
    };
    let response: any;
    try {
      response = await this.apiManager.apis["AuthApi"].apiAuthTokenPost(post);
      if (response.status === 200) {
        await this.setAuthState(response.data, rememberMe);
      } else {
        this.user.value = null;
        this.authenticated.value = false;
        if (response.errors) {
          throw response.errors;
        }
      }
    } catch (error) {
      this.user.value = null;
      this.authenticated.value = false;

      console.log("error caught, rethrow");
      let errorMessage = "Error in login";
      if (error.response && error.response.data && error.response.data.errors) {
        errorMessage +=
          ": " +
          error.response.data.errors
            .map((e) => e.description || e.message || e)
            .join(", ");
      } else if (Array.isArray(error)) {
        errorMessage +=
          ": " + error.map((e) => e.description || e.message || e).join(", ");
      } else if (error.message) {
        errorMessage += ": " + error.message;
      }
      console.log(errorMessage);

      throw new Error("Error in login: " + errorMessage);
    } finally {
      this.loading.value = false;
    }
  }
  // resetPassword method
  async resetPassword(newPassword: string, token: string, email: string) {
    const apiManager = ApiManager.getInstance();
    if (!token) {
      token = "Bearer " + useCookie("accessToken")?.value ?? "";
    }
    if (token === "Bearer ") {
      // no token, send them to the email reset page
      //route to it

      this.router.push("/auth/forgotpasswordtoken");
      return;
    }
    const dto = {
      newPassword: newPassword,
      token: token,
      email: email,
    } as ResetPasswordDto;

    try {
      const response =
        await apiManager.apis["UserApi"].apiUserResetPasswordPost(dto);

      if (response.status === 200) {
        console.log("got 200");

        EventBus.emit("toast", {
          type: "success",
          message: "User password reset successfully!",
        });
        return true;
      } else {
        if (response.data?.errors) {
          response.data.errors.forEach((error) => {
            EventBus.emit("toast", {
              type: "error",
              message: `Error in user password reset ${error.code}: ${error.description}`,
            });
          });
        }
        return false;
      }
    } catch (error) {
      console.log("error" + this.safeStringify(error.message));
      EventBus.emit("toast", {
        type: "error",
        message: "Error in user password reset: " + error.message,
      });
      return false;
    }
  }
  async populateUserFromToken() {
    if (this.user.value) {
      this.loading.value = false;
      return;
    }
    this.loading.value = true;

    const apiManager = ApiManager.getInstance();
    let token = useCookie("accessToken");

    if (token.value) {
      try {
        const response = await apiManager.apis["UserApi"].whoami();
        if (response.status === 200) {
          await this.setAuthState(response.data);
        }
      } catch (error) {
        this.user.value = null;
        this.authenticated.value = false;
      }
    }
    // no token, no user
    this.loading.value = false;
  }

  async forgotPassword(email: string) {
    console.log("forgot password");

    this.loading.value = true;

    const apiManager = ApiManager.getInstance();
    try {
      const forgotPasswordRequest: ForgotPasswordRequest = { email };
      const response = await apiManager.apis[
        "UserApi"
      ].apiUserForgotPasswordPost(forgotPasswordRequest);
      if (response.status === 200) {
        console.log("Password reset email sent");
      } else {
        if (response.data.errors) {
          throw response.data.errors;
        }
      }
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.loading.value = false;
    }
  }

  async logout() {
    this.unSetAuthState();
    const serverEnvironmentStore = useServerEnvironmentStore();
    serverEnvironmentStore.reset();

    if (!this.authenticated.value) {
      this.router.push("/auth/");
    }
  }
}

export default AuthManager;
